import { t } from 'i18next';
import { AppController, Route } from '../AppController';
import { Optional } from '../types';
import { waitFor } from '../utils';
import { ElementUIState } from './UIController/UIController';

interface NavOpts<T> {
  // Clear the history if set to true
  wipeCrumbs?: boolean;
  // Pushes a route before itself
  pushParent?: T;
  // This is used for pages which load items based on a param like user profile, trading item
  routeParam?: string;
}

export class NavController {
  private history: (Route | 'Clicker' | 'Modal')[] = ['Clicker'];
  private historyParams: Record<number, string> = {};
  private nextOpts?: any;

  private suspended = false;

  get currentRoute() {
    return this.history[this.history.length - 1];
  }

  get prevRoute() {
    return this.history[this.history.length - 2];
  }

  constructor(private app: AppController) {
    app.ui.drawer.onAnyModalClose(this.handleModalReset);
  }

  private handleModalReset = () => {
    // If a modal is closed via UI make sure to reset the nav state
    if (this.currentRoute === 'Modal') {
      this.history.pop();
    }
  };

  private closeKeyboard = () => {
    const inputs = document.querySelectorAll('input');
    inputs.forEach((input) => input.blur());
  };

  private closePages = () => {
    // Hide all view components
    Object.values(this.app.views).forEach((component) => component.hide());
    // Show Telegram close button
    Telegram.WebApp.BackButton.hide();
  };

  private navigate = (route: Route | 'Clicker', prop?: string) => {
    // We don't have navigation back from clicker, so any time we go to clicker we wipe history
    if (route === 'Clicker') {
      this.history = ['Clicker'];
      this.closePages();
      return;
    }

    // Show black overlay
    this.app.ui.setClickerUIState({ outOfViewOverlay: ElementUIState.Normal });

    // current page = route when navigating back
    if (this.currentRoute !== route) {
      // Hide current view
      const view = this.getView(this.currentRoute);
      if (view) {
        this.app.views[view].hide();
      }
      // Just before we add the new route, if we should wipe, set back to default
      if (this.nextOpts?.wipeCrumbs) {
        this.history = ['Clicker'];
      }
      if (this.nextOpts?.pushParent) {
        this.history.push(this.nextOpts.pushParent);
      }
      this.history.push(route);
      if (prop) {
        this.historyParams[this.history.length] = prop;
      }
    }
    // return the current view so we can do `setData`, etc
    return this.app.views[route].show(this.nextOpts);
  };

  /**
   * Use this function to navigate to a specific view; use (optional) `opts.wipeCrumbs` to reset history
   * @param route view name registered in AppController
   * @param opts view specific opts passed during view.show()
   * @returns
   */
  goTo = (
    route: Route | 'Clicker',
    opts: any & NavOpts<Route> = undefined, // @TODO: Fix types
  ) => {
    if (this.suspended || route === this.currentRoute) {
      return;
    }

    if (route === 'Clicker') {
      this.app.ui.setClickerUIState({
        outOfViewOverlay: ElementUIState.Hidden,
      });
    }

    // If we are navigating from within a modal, remove the modal from history
    if (this.currentRoute === 'Modal') {
      this.history.pop();
    }
    this.nextOpts = opts;
    return this.navigate(route, opts?.routeParam);
  };

  /**
   * This function is called on the back button; removes current route from history
   * and navigate to new current route
   * @returns
   */
  back = async () => {
    if (this.suspended) {
      return;
    }
    // whenever we tap on back button, make sure to close any active input/keyboard
    this.closeKeyboard();

    // Alaways go back to clicker
    if (this.history.length <= 1) {
      return;
    }

    // Remove current route
    const prevRoute = this.history.pop();
    if (this.currentRoute === 'Clicker') {
      this.app.ui.setClickerUIState({
        outOfViewOverlay: ElementUIState.Hidden,
      });
    }

    if (prevRoute === 'Modal') {
      this.app.ui.drawer.close();
      return;
    }

    const routeParam = this.historyParams[this.history.length];
    const view = this.getView(prevRoute);

    if (routeParam) {
      delete this.historyParams[this.history.length];
      await this.setupRouteWithParam(routeParam);
    }

    if (view) {
      this.app.views[view].hide();
    }
    // Go to new current route
    if (this.currentRoute !== 'Modal') {
      this.navigate(this.currentRoute);
    }
  };

  /**
   * This function takes care of re applying page props when navigating back. It returns
   * false to prevent the default back navigation behaviour.
   * Extend this function if a new page is introduced that requires custom props
   * @param param
   * @returns false if the navigation is handled by
   */
  private setupRouteWithParam = async (param?: string) => {
    if (!param || this.suspended) {
      return;
    }
    this.suspended = true;
    switch (this.currentRoute) {
      case 'ProfilePage':
        await this.app.profile.setProfile(param);
        break;
      case 'TradingTokenPage':
        await this.app.trading.setOffchainToken(param);
        break;
    }
    this.suspended = false;
  };

  /**
   * Use this function when showing a modal which intereferes with navigation. When
   * you need the back button to close the modal and go back to page below modal.
   *
   */
  showingBlockingModal = () => {
    if (this.currentRoute !== 'Modal') {
      this.history.push('Modal');
    }
  };

  private goToAsyncPage = async (
    route: Route,
    pageSetupFn: () => Promise<string | undefined>,
  ) => {
    if (this.suspended) {
      return;
    }
    this.suspended = true;
    const routeParam = await pageSetupFn();
    // await this.app.profile.setProfile(userId);
    this.suspended = false;
    if (routeParam) {
      this.goTo(route, { routeParam });
    }
  };

  /**
   * Use this function to navigate to a profile; It takes care of waiting for the ProfileController
   * to be ready before it shows the page while suspending any navigation until this is completed
   */
  goToProfile = async (userId = this.app.state.id) => {
    this.goToAsyncPage('ProfilePage', async () => {
      await this.app.profile.setProfile(userId);
      return userId;
    });
  };

  /**
   * Use this function to navigate to a profile; It takes care of waiting for the ProfileController
   * to be ready before it shows the page while suspending any navigation until this is completed
   */
  goToTradingToken = async (tokenId: string) => {
    this.goToAsyncPage('TradingTokenPage', () =>
      this.app.trading.setOffchainToken(tokenId),
    );
  };

  /**
   * Call this function when the app is ready passing in the query params related to deep linking;
   * By default it navigates to a route received if valid. But also has a hook for custom deep linking
   * when values are need to be set before navigation, etc.
   * @param maybeRoute value extracted from the query params when loading the app
   * @param dlOpts value extracted from the query params when loading the app
   * @returns void
   */
  deepLink = async (maybeRoute?: string, dlOpts?: string) => {
    if (!maybeRoute) {
      return;
    }
    const view = this.getView(maybeRoute as Route | 'Clicker' | 'Modal');
    // if we have a valid view for deep linking then navigate to route
    if (view) {
      const customDlFn = customDeepLinkCfg[view];

      let opts: any = undefined;
      if (typeof dlOpts === 'string') {
        try {
          opts = JSON.parse(dlOpts);
        } catch {
          /* fail silently */
        }
      } else if (typeof dlOpts === 'object') {
        opts = dlOpts;
      }

      // The custom function returns undefined if it handles the whole navigation within
      // Or returns the nav controller instance if it only handles the pre navigation setup like setting up some state prior to nav
      if (customDlFn) {
        const reroute = await customDlFn(this.app, opts);
        if (reroute) {
          this.goTo(reroute);
        }
        return;
      }

      this.goTo(view);
    }
  };

  getDeeplinkOpts = (route: DeeplinkRoute) => {
    return deeplinkRoutes[route](this.app);
  };

  private getView = (
    route?: Route | 'Clicker' | 'Modal',
  ): Route | undefined => {
    if (Object.keys(this.app.views).includes(route || '')) {
      return route as Route;
    }
    return undefined;
  };
}

/**
 * DEFINE HERE CUSTOM SPECIFIC DEEPLINK CONFIG
 */
const deeplinkRoutes = {
  TradingTokenPage: (app: AppController) => {
    return {
      offchainTokenId: app.trading.currentOffchainToken?.id,
    };
  },
  ProfilePage: (app: AppController) => {
    return {
      userId: app.profile.current?.id,
    };
  },
};
type DeeplinkRoute = keyof typeof deeplinkRoutes;

/**
 * Handles coming into the app via a deeplink url; Make any preparations needed
 * and return route to navigate to or undefined if you are handling all navigation
 * within custom handler
 */
type CustomDeeplinkFn<T> = (
  app: AppController,
  opts?: T,
) => Promise<Optional<keyof typeof app.views>>;

const TradingTokenPage: CustomDeeplinkFn<
  ReturnType<typeof deeplinkRoutes.TradingTokenPage>
> = async (app, opts) => {
  app.ui.suspendInitialModals();
  if (app.trading.isActive) {
    if (opts?.offchainTokenId) {
      app.nav.goToTradingToken(opts.offchainTokenId);
    }
  } else {
    app.views.Toast.setData({
      text: t('trading_not_active_toast'),
      hidePurchaseText: true,
    }).show();
    await waitFor(2000);
  }
  app.ui.resumeInitialModals();
  return undefined;
};

const ProfilePage: CustomDeeplinkFn<
  ReturnType<typeof deeplinkRoutes.ProfilePage>
> = async (app, opts) => {
  if (opts?.userId) {
    await app.nav.goToProfile(opts.userId);
  }
  return undefined;
};

type DeeplinkHandlerFns = typeof TradingTokenPage | typeof ProfilePage;

const customDeepLinkCfg: Record<string, DeeplinkHandlerFns> = {
  TradingTokenPage,
  ProfilePage,
};
